import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { PerInfoBox } from '../../../../components/custom_components/InfoBox'
import { PerInfoBoxNumberElement } from '../../../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { MeetingHrFeedbackResult, MeetingStatus } from '../../../../types'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import '../../appraisal/index.less'
import { Checkbox, Col, Divider, Spin, Tooltip } from 'antd'
import PerText from '../../../../components/custom_components/Text'
import { areFeedbackTabFiltersEquals, hrSupportFeedbackTabInitialFilter } from './feedback-util'
import PerDatePicker from '../../../../components/custom_components/DatePicker'
import { useAuth } from '../../../../context/auth-context'
import moment, { Moment } from 'moment-timezone'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { useHrSupportStore } from '../HrSupportStore'
import shallow from 'zustand/shallow'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerButtonCircle from '../../../../components/custom_components/ButtonCircle'

interface Props {
  counter?: number
  counterLoading: boolean
}

export const HrSupportFeedbackInfoBox: FC<Props> = ({ counter, counterLoading }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [filters, setFilters] = useHrSupportStore((state) => [state.feedbackTabFilters, state.setFeedbackTabFilters], shallow)

  const meetingDisputeValues: string[] = Object.values(MeetingHrFeedbackResult)
  const meetingDisputeMappedValues: string[] = Object.values(MeetingHrFeedbackResult).map((el) => el.replace(/([a-z])([A-Z])/g, '$1 $2'))

  const onFeedbackResolutionChange = (e: CheckboxChangeEvent) => {
    setFilters({
      ...filters,
      feedbackResolutions: e.target.checked
        ? filters.feedbackResolutions.includes(e.target.value)
          ? filters.feedbackResolutions
          : [...filters.feedbackResolutions, e.target.value]
        : filters.feedbackResolutions.filter((el) => el !== e.target.value),
    })
  }

  const onMeetingStatusChange = (e: CheckboxChangeEvent) => {
    setFilters({
      ...filters,
      meetingStatuses: e.target.checked
        ? filters.meetingStatuses.includes(e.target.value)
          ? filters.meetingStatuses
          : [...filters.meetingStatuses, e.target.value]
        : filters.meetingStatuses.filter((el) => el !== e.target.value),
    })
  }

  return (
    <PerInfoBox>
      <PerFlexRow align="middle" justify="space-between" width="fullWidth" className="fullHeight">
        <Col span={19} className="fullHeight">
          <PerFlexRow direction="column" justify="space-between" className="fullHeight" width="fullWidth">
            <PerFlexRow justify="space-between">
              <PerText appearance="tile-heading">{t('companySettingsView.feedbackTab.infoBox.heading')}</PerText>
              <Tooltip placement="left" title={t('companySettingsView.appraisalTab.filter.reset')}>
                <PerButtonCircle
                  color="error"
                  icon="delete_fill"
                  variant="pastel"
                  onClick={() => {
                    setFilters(hrSupportFeedbackTabInitialFilter)
                  }}
                  disabled={areFeedbackTabFiltersEquals(hrSupportFeedbackTabInitialFilter, filters)}
                />
              </Tooltip>
            </PerFlexRow>

            <PerFlexRow align="middle" justify="space-between" className="fullHeight">
              <Col span={9} className="fullHeight">
                <PerFlexRow direction="column" width="fullWidth" className="fullHeight">
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxs">
                    {t('companySettingsView.feedbackTab.infoBox.meetingResolution')}
                  </PerText>

                  <PerFlexRow width="fullWidth" className="fullHeight">
                    <Col span={12}>
                      <PerFlexRow direction="column">
                        <Checkbox
                          onChange={onFeedbackResolutionChange}
                          checked={filters.feedbackResolutions.includes(meetingDisputeValues[1] as MeetingHrFeedbackResult)}
                          value={meetingDisputeValues[1]}
                        >
                          <div style={{ marginLeft: '-8px' }}>
                            <PerText appearance="label" color="secondary" fontWeight="normal">
                              {meetingDisputeMappedValues[1]}
                            </PerText>
                          </div>
                        </Checkbox>
                        <PerSpacer mb="xxs" />
                        <Checkbox
                          onChange={onFeedbackResolutionChange}
                          checked={filters.feedbackResolutions.includes(meetingDisputeValues[0] as MeetingHrFeedbackResult)}
                          value={meetingDisputeValues[0]}
                        >
                          <div style={{ marginLeft: '-8px' }}>
                            <PerText appearance="label" color="secondary" fontWeight="normal">
                              {meetingDisputeMappedValues[0]}
                            </PerText>
                          </div>
                        </Checkbox>
                      </PerFlexRow>
                    </Col>

                    <Col span={12}>
                      <PerFlexRow direction="column">
                        <Checkbox
                          onChange={onFeedbackResolutionChange}
                          checked={filters.feedbackResolutions.includes(meetingDisputeValues[3] as MeetingHrFeedbackResult)}
                          value={meetingDisputeValues[3]}
                        >
                          <div style={{ marginLeft: '-8px' }}>
                            <PerText appearance="label" color="secondary" fontWeight="normal">
                              {t('companySettingsView.feedbackTab.infoBox.resolved')}
                            </PerText>
                          </div>
                        </Checkbox>
                        <PerSpacer mb="xxs" />
                        <Checkbox
                          onChange={onFeedbackResolutionChange}
                          checked={filters.feedbackResolutions.includes(meetingDisputeValues[2] as MeetingHrFeedbackResult)}
                          value={meetingDisputeValues[2]}
                        >
                          <div style={{ marginLeft: '-8px' }}>
                            <PerText appearance="label" color="secondary" fontWeight="normal">
                              {t('companySettingsView.feedbackTab.infoBox.pendingFeedback')}
                            </PerText>
                          </div>
                        </Checkbox>
                      </PerFlexRow>
                    </Col>
                  </PerFlexRow>
                </PerFlexRow>
              </Col>

              <Col span={5} className="fullHeight">
                <PerFlexRow direction="column" width="fullWidth" className="fullHeight">
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxs">
                    {t('companySettingsView.feedbackTab.infoBox.meetingStatus')}
                  </PerText>
                  <Checkbox
                    onChange={onMeetingStatusChange}
                    checked={filters.meetingStatuses.includes(MeetingStatus.Confirmed)}
                    value={MeetingStatus.Confirmed}
                  >
                    <div style={{ marginLeft: '-8px' }}>
                      <PerText appearance="label" color="secondary" fontWeight="normal">
                        {MeetingStatus.Confirmed.replace(/([a-z])([A-Z])/g, '$1 $2')}
                      </PerText>
                    </div>
                  </Checkbox>
                  <PerSpacer mb="xxs" />
                  <Checkbox
                    onChange={onMeetingStatusChange}
                    checked={filters.meetingStatuses.includes(MeetingStatus.DisputedConfirmed)}
                    value={MeetingStatus.DisputedConfirmed}
                  >
                    <div style={{ marginLeft: '-8px' }}>
                      <PerText appearance="label" color="secondary" fontWeight="normal">
                        {MeetingStatus.DisputedConfirmed.replace(/([a-z])([A-Z])/g, '$1 $2')}
                      </PerText>
                    </div>
                  </Checkbox>
                </PerFlexRow>
              </Col>

              <Col span={5} className="fullHeight">
                <PerFlexRow mb="xs">
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                    {t('companySettingsView.feedbackTab.infoBox.companyFromDate')}
                  </PerText>
                  <PerDatePicker
                    showToday={false}
                    inputDate={filters.companyFromDate || ''}
                    dateFormat="DMY"
                    timeZoneAndLocale={user}
                    size="small"
                    width={'100%'}
                    disabledDate={(current: Moment | null) => !!current && current < moment().subtract(5, 'year')}
                    onDateChanged={(date) => setFilters({ ...filters, companyFromDate: date })}
                    allowClear={true}
                  />
                </PerFlexRow>
                <PerFlexRow>
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                    {t('companySettingsView.feedbackTab.infoBox.employeeFromDate')}
                  </PerText>
                  <PerDatePicker
                    showToday={false}
                    inputDate={filters.employeeFromDate || ''}
                    dateFormat="DMY"
                    timeZoneAndLocale={user}
                    size="small"
                    width={'100%'}
                    disabledDate={(current: Moment | null) => !!current && current < moment().subtract(5, 'year')}
                    onDateChanged={(date) => setFilters({ ...filters, employeeFromDate: date })}
                    allowClear={true}
                  />
                </PerFlexRow>
              </Col>

              <Col span={5} className="fullHeight">
                <PerFlexRow mb="xs">
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                    {t('companySettingsView.feedbackTab.infoBox.companyToDate')}
                  </PerText>
                  <PerDatePicker
                    showToday={false}
                    inputDate={filters.companyToDate || ''}
                    dateFormat="DMY"
                    timeZoneAndLocale={user}
                    size="small"
                    width={'100%'}
                    disabledDate={(current: Moment | null) => !!current && current > moment().add(1, 'year')}
                    onDateChanged={(date) => setFilters({ ...filters, companyToDate: date })}
                    allowClear={true}
                  />
                </PerFlexRow>
                <PerFlexRow>
                  <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                    {t('companySettingsView.feedbackTab.infoBox.employeeToDate')}
                  </PerText>
                  <PerDatePicker
                    showToday={false}
                    inputDate={filters.employeeToDate || ''}
                    dateFormat="DMY"
                    timeZoneAndLocale={user}
                    size="small"
                    width={'100%'}
                    disabledDate={(current: Moment | null) => !!current && current > moment().add(1, 'year')}
                    onDateChanged={(date) => setFilters({ ...filters, employeeToDate: date })}
                    allowClear={true}
                  />
                </PerFlexRow>
              </Col>
            </PerFlexRow>
          </PerFlexRow>
        </Col>

        <Divider type="vertical" className="fullHeight" />

        <Col span={4} className="fullHeight">
          <PerFlexRow direction="column" justify="space-between" className="fullHeight">
            <PerText appearance="tile-heading">{t('companySettingsView.feedbackTab.infoBox.counters')}</PerText>
            <PerFlexRow direction="column" noWrap className={counterLoading ? 'fullHeight' : undefined}>
              {counterLoading ? (
                <PerFlexRow align="middle" justify="center" width="fullWidth" className="fullHeight">
                  <Spin size="large" spinning />
                </PerFlexRow>
              ) : (
                <>
                  <PerInfoBoxNumberElement number={`${counter !== undefined ? counter : '?'}`} />
                </>
              )}
            </PerFlexRow>
          </PerFlexRow>
        </Col>
      </PerFlexRow>
    </PerInfoBox>
  )
}
